/**
 * MarketplaceLeadsWidget — widget w dashboardzie hodowcy pokazujący leady z katalogu.
 *
 * Dane z: GET /api/panel/marketplace-stats
 * - Licznik this_week dla: lead_submit / tel_click / whatsapp_click / view_click
 * - Ostatnie 5 leadów (universal_leads)
 *
 * P0 MVP per user spec.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/utils/api';
import { Phone, MessageCircle, Mail, Eye, ArrowRight, Inbox, Loader2 } from 'lucide-react';

function formatRelative(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'przed chwilą';
  if (diff < 3600) return `${Math.floor(diff / 60)} min temu`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h temu`;
  const days = Math.floor(diff / 86400);
  return `${days} ${days === 1 ? 'dzień' : 'dni'} temu`;
}

const SOURCE_ICON = {
  marketplace_quick_lead: { Icon: Inbox, label: 'Z katalogu', color: 'text-orange-600' },
  litter: { Icon: Inbox, label: 'Miot', color: 'text-emerald-600' },
  kennel: { Icon: Inbox, label: 'Hodowla', color: 'text-blue-600' },
  stud: { Icon: Inbox, label: 'Reproduktor', color: 'text-purple-600' },
  direct: { Icon: Inbox, label: 'Bezpośrednio', color: 'text-zinc-500' },
};

export default function MarketplaceLeadsWidget({ authConfig }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/panel/marketplace-stats`, authConfig)
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [authConfig]);

  if (loading) {
    return (
      <div className="mb-6 bg-white border border-zinc-200 rounded-2xl p-5 flex items-center justify-center text-zinc-400">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  if (!data || data.slugs_count === 0) {
    return null; // user bez aktywnych ogłoszeń — widget niepotrzebny
  }

  const w = data.this_week || {};
  const totalInteractions = (w.lead_submit || 0) + (w.tel_click || 0) + (w.whatsapp_click || 0);

  return (
    <section
      className="mb-6 bg-white border border-zinc-200 rounded-2xl p-5 shadow-sm"
      data-testid="marketplace-leads-widget"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-bold text-zinc-900">Leady z katalogu</h3>
          <p className="text-[12px] text-zinc-500">Ten tydzień · ostatnie 7 dni</p>
        </div>
        <Link
          to="/panel/leady"
          className="text-[12px] font-semibold text-[#F47A3B] hover:text-[#E06A2E] inline-flex items-center gap-1"
          data-testid="marketplace-widget-see-all"
        >
          Wszystkie leady <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      {/* Liczniki */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <StatCard
          icon={<Inbox className="w-4 h-4" />}
          label="Zapytania"
          value={w.lead_submit || 0}
          accent="bg-orange-50 text-orange-700 border-orange-200"
          testid="stat-lead-submit"
        />
        <StatCard
          icon={<Phone className="w-4 h-4" />}
          label="Klik tel"
          value={w.tel_click || 0}
          accent="bg-sky-50 text-sky-700 border-sky-200"
          testid="stat-tel-click"
        />
        <StatCard
          icon={<MessageCircle className="w-4 h-4" />}
          label="Klik WA"
          value={w.whatsapp_click || 0}
          accent="bg-emerald-50 text-emerald-700 border-emerald-200"
          testid="stat-wa-click"
        />
        <StatCard
          icon={<Eye className="w-4 h-4" />}
          label="Odwiedziny"
          value={w.view_click || 0}
          accent="bg-zinc-50 text-zinc-700 border-zinc-200"
          testid="stat-view-click"
        />
      </div>

      {/* Ostatnie leady */}
      {data.recent_leads?.length > 0 ? (
        <div className="border-t border-zinc-100 pt-3">
          <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold mb-2">
            Ostatnie zapytania
          </div>
          <ul className="space-y-2" data-testid="marketplace-recent-leads">
            {data.recent_leads.map((lead, i) => {
              const meta = SOURCE_ICON[lead.source_type] || SOURCE_ICON.direct;
              return (
                <li
                  key={lead.id || i}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-50 transition"
                  data-testid={`recent-lead-${i}`}
                >
                  <div className={`w-8 h-8 rounded-lg bg-zinc-50 border border-zinc-100 flex items-center justify-center shrink-0 ${meta.color}`}>
                    <meta.Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-zinc-800 truncate">
                      {lead.name || 'Anonimowe zapytanie'}
                    </div>
                    <div className="text-[11px] text-zinc-500 truncate">
                      {meta.label} · {formatRelative(lead.created_at)}
                      {lead.phone && ' · '}{lead.phone}
                    </div>
                  </div>
                  <Link
                    to="/panel/leady"
                    className="text-[11px] font-semibold text-[#F47A3B] hover:text-[#E06A2E]"
                  >
                    Otwórz →
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ) : totalInteractions > 0 ? (
        <div className="border-t border-zinc-100 pt-3">
          <p className="text-[12px] text-zinc-500">
            Ktoś dziś klikał Twoje ogłoszenia — nikt jeszcze nie wysłał zapytania.
            Rozważ dodanie zdjęć szczeniąt lub obniżenie ceny.
          </p>
        </div>
      ) : (
        <div className="border-t border-zinc-100 pt-3">
          <p className="text-[12px] text-zinc-500">
            Brak zapytań w tym tygodniu. <Link to="/panel/kampania" className="text-[#F47A3B] font-semibold">Odpowiedz szybciej — więcej klientów →</Link>
          </p>
        </div>
      )}
    </section>
  );
}

function StatCard({ icon, label, value, accent, testid }) {
  return (
    <div
      className={`rounded-xl border p-3 ${accent}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 text-[11px] font-semibold opacity-80">
        {icon}
        <span>{label}</span>
      </div>
      <div className="text-2xl font-black mt-1 tabular-nums">{value}</div>
    </div>
  );
}
